import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { impersonationService } from '@/services/supabase/impersonationService';

/** Intervalo do heartbeat: o banco guarda 1 linha por usuário por minuto */
const INTERVALO_MS = 60_000;
/** Sem clique/tecla/rolagem há mais que isso → usuário considerado ausente */
const OCIOSO_APOS_MS = 5 * 60_000;

const EVENTOS_INTERACAO = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove'] as const;

/**
 * Troca ids na rota por ":id" para agrupar telas na dashboard
 * (ex.: /clientes/123/editar → /clientes/:id/editar).
 */
export function normalizarRota(pathname: string): string {
  return pathname
    .split('/')
    .map(parte =>
      /^\d+$/.test(parte) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(parte)
        ? ':id'
        : parte
    )
    .join('/') || '/';
}

function detectarDispositivo(): string {
  return window.matchMedia('(max-width: 767px)').matches ? 'celular' : 'desktop';
}

/**
 * Registra minutos de uso ativo (aba visível + interação recente) para a
 * dashboard "Uso do sistema" do SUPER_ADMIN. Não registra durante "entrar como".
 */
export function useRastreamentoUso(ativo: boolean) {
  const location = useLocation();
  const rotaRef = useRef(normalizarRota(location.pathname));
  const ultimaInteracaoRef = useRef(Date.now());

  useEffect(() => {
    rotaRef.current = normalizarRota(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!ativo || impersonationService.estaImpersonando()) return;

    let ultimoMousemove = 0;
    const marcarInteracao = (evento: Event) => {
      // mousemove dispara dezenas de vezes por segundo; basta 1 registro a cada 5s
      if (evento.type === 'mousemove') {
        const agora = Date.now();
        if (agora - ultimoMousemove < 5_000) return;
        ultimoMousemove = agora;
      }
      ultimaInteracaoRef.current = Date.now();
    };

    const registrar = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - ultimaInteracaoRef.current > OCIOSO_APOS_MS) return;
      // RPC nova, ainda fora de integrations/supabase/types.ts
      (supabase as unknown as SupabaseClient)
        .rpc('registrar_minuto_ativo', {
          p_rota: rotaRef.current,
          p_dispositivo: detectarDispositivo(),
        })
        .then(({ error }) => {
          if (error) console.warn('[useRastreamentoUso] falha ao registrar minuto ativo:', error.message);
        });
    };

    EVENTOS_INTERACAO.forEach(evento =>
      window.addEventListener(evento, marcarInteracao, { passive: true })
    );
    const aoVoltarParaAba = () => {
      if (document.visibilityState === 'visible') {
        ultimaInteracaoRef.current = Date.now();
        registrar();
      }
    };
    document.addEventListener('visibilitychange', aoVoltarParaAba);

    registrar();
    const intervalo = window.setInterval(registrar, INTERVALO_MS);

    return () => {
      window.clearInterval(intervalo);
      EVENTOS_INTERACAO.forEach(evento => window.removeEventListener(evento, marcarInteracao));
      document.removeEventListener('visibilitychange', aoVoltarParaAba);
    };
  }, [ativo]);
}
